'use client';
import React, { useState, useEffect } from 'react';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { getStripe } from '@/lib/stripe/client';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────
interface FeeBreakdown {
  consultantRate: number;    // hourly rate
  sessionFee: number;        // rate × duration multiplier
  platformFee: number;       // 5% of session fee
  totalDue: number;          // sessionFee + platformFee
  durationLabel: string;
  consultantName: string;
}

interface SessionPaymentFormProps {
  fees: FeeBreakdown;
  onSuccess: (paymentIntentId: string) => void;
  onBack: () => void;
  /** Called to create a PaymentIntent on the server and return clientSecret */
  onCreatePaymentIntent: () => Promise<{ clientSecret: string; paymentIntentId: string }>;
}

// ─── Stripe Element Styles ────────────────────────────────────────────────────
const elementStyle = {
  base: {
    fontSize: '14px',
    color: '#1a1a2e',
    fontFamily: 'inherit',
    '::placeholder': { color: '#9ca3af' },
  },
  invalid: { color: '#ef4444' },
};

// ─── Inner Form (must be inside <Elements>) ───────────────────────────────────
function PaymentFormInner({
  fees,
  onSuccess,
  onBack,
  onCreatePaymentIntent,
}: SessionPaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();

  const [cardholderName, setCardholderName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [intentLoading, setIntentLoading] = useState(true);
  const [intentError, setIntentError] = useState<string | null>(null);

  // Create payment intent on mount
  useEffect(() => {
    let cancelled = false;
    setIntentLoading(true);
    setIntentError(null);
    onCreatePaymentIntent()
      .then(({ clientSecret: cs, paymentIntentId: pid }) => {
        if (!cancelled) {
          setClientSecret(cs);
          setPaymentIntentId(pid);
          setIntentLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setIntentError(err instanceof Error ? err.message : 'Failed to initialise payment');
          setIntentLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) return;

    setProcessing(true);
    setErrorMsg(null);

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardNumber,
        billing_details: { name: cardholderName.trim() || undefined },
      },
    });

    if (error) {
      setErrorMsg(error.message ?? 'Payment failed. Please try again.');
      setProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntentId ?? paymentIntent.id);
    } else {
      setErrorMsg('Payment was not completed. Please try again.');
      setProcessing(false);
    }
  };

  if (intentLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin mb-4" />
        <p className="text-sm text-muted-foreground">Setting up secure payment…</p>
      </div>
    );
  }

  if (intentError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
          <Icon name="ExclamationCircleIcon" size={28} className="text-red-500" />
        </div>
        <h3 className="text-base font-600 text-foreground mb-2">Payment Setup Failed</h3>
        <p className="text-sm text-muted-foreground mb-4">{intentError}</p>
        <button
          onClick={onBack}
          className="text-sm text-primary font-600 hover:underline flex items-center gap-1"
        >
          <Icon name="ArrowLeftIcon" size={14} />
          Go back
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Fee Breakdown Card */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5">
        <h3 className="text-sm font-700 text-foreground mb-4 flex items-center gap-2">
          <Icon name="ReceiptPercentIcon" size={16} className="text-primary" />
          Payment Summary
        </h3>
        <div className="space-y-2.5 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Consultant rate</span>
            <span className="font-600 text-foreground">${fees.consultantRate.toFixed(2)}/hr</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Session fee ({fees.durationLabel})</span>
            <span className="font-600 text-foreground">${fees.sessionFee.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Platform fee (5%)</span>
            <span className="font-600 text-foreground">${fees.platformFee.toFixed(2)}</span>
          </div>
          <div className="border-t border-primary/20 pt-3 flex justify-between items-center">
            <span className="font-700 text-foreground">Total Due</span>
            <span className="font-700 text-primary text-xl">${fees.totalDue.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Card Details */}
      <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <h3 className="text-sm font-700 text-foreground flex items-center gap-2">
          <Icon name="CreditCardIcon" size={16} className="text-primary" />
          Card Details
        </h3>

        {/* Cardholder Name */}
        <div>
          <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1.5 block">
            Cardholder Name
          </label>
          <input
            type="text"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            placeholder="Name on card"
            className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
        </div>

        {/* Card Number */}
        <div>
          <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1.5 block">
            Card Number
          </label>
          <div className="px-4 py-3 rounded-xl border border-border bg-background focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition-colors">
            <CardNumberElement options={{ style: elementStyle, showIcon: true }} />
          </div>
        </div>

        {/* Expiry + CVC */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1.5 block">
              Expiry Date
            </label>
            <div className="px-4 py-3 rounded-xl border border-border bg-background focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition-colors">
              <CardExpiryElement options={{ style: elementStyle }} />
            </div>
          </div>
          <div>
            <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1.5 block">
              CVC
            </label>
            <div className="px-4 py-3 rounded-xl border border-border bg-background focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition-colors">
              <CardCvcElement options={{ style: elementStyle }} />
            </div>
          </div>
        </div>

        {/* Error */}
        {errorMsg && (
          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50 border border-red-200">
            <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errorMsg}</p>
          </div>
        )}

        {/* Security note */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="LockClosedIcon" size={13} className="text-primary flex-shrink-0" />
          Your payment is encrypted and processed securely via Stripe.
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        <button
          type="submit"
          disabled={processing || !stripe}
          className={`w-full py-3.5 rounded-xl text-sm font-700 transition-all duration-150 flex items-center justify-center gap-2 ${
            processing || !stripe
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'bg-primary text-white hover:bg-primary/90 shadow-md hover:shadow-lg'
          }`}
        >
          {processing ? (
            <>
              <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
              Processing…
            </>
          ) : (
            <>
              <Icon name="LockClosedIcon" size={16} />
              Pay ${fees.totalDue.toFixed(2)} &amp; Confirm Booking
            </>
          )}
        </button>

        <button
          type="button"
          onClick={onBack}
          disabled={processing}
          className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="ArrowLeftIcon" size={14} />
          Back to Review
        </button>
      </div>
    </form>
  );
}

// ─── Exported Wrapper (provides Elements context) ─────────────────────────────
export default function SessionPaymentForm(props: SessionPaymentFormProps) {
  return (
    <Elements stripe={getStripe()}>
      <PaymentFormInner {...props} />
    </Elements>
  );
}
